import { datadogRum } from "@datadog/browser-rum";
import { motion } from "framer-motion";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import { getGlobalData } from "utils/api";
import { getLocalizedPaths } from "utils/localize";

import AppSummary from "@/components/ccApplicationSections/app-summary";
import Expenses from "@/components/ccApplicationSections/expenses";
import Personal from "@/components/ccApplicationSections/personal";
import Verify from "@/components/ccApplicationSections/verify";
import Seo from "@/components/elements/seo";
import Layout from "@/components/layout";
import {
  AccordionSummary,
  LargeAccordion,
} from "@/components/onboardingSections/inputs";
import { StepperOnboarding as Stepper } from "@/components/onboardingSections/stepper";
import styles from "@/styles/onboarding.module.scss";
import { isNewOnboardingEnabled } from "@/utils/onboarding-feature-flag";

export const ADDRESS_DATA_FIELDS = [
  "sub_building_number",
  "sub_building_name",
  "building_name",
  "building_number",
  "thoroughfare",
  "town_or_city",
];

export const ADDRESS_DATA_FIELDS_EMPTY = Object.fromEntries(
  ADDRESS_DATA_FIELDS.map(field => [field, ""]),
);

export default function Apply({ metadata, global, pageContext }) {
  const effectHasRun_ref = useRef(false);
  const router = useRouter();
  const [metaTitle, setMetaTitle] = useState(metadata);
  const [expanded, setExpanded] = useState("panel1");
  const [personalComplete, setPersonalComplete] = useState(false);
  const [verifyComplete, setVerifyComplete] = useState(false);
  const [expensesComplete, setExpenesComplete] = useState(false);
  const [addressData, setAddressData] = useState({
    ...ADDRESS_DATA_FIELDS_EMPTY,
    postcode: "",
    country: "",
    move_in_date: "",
  });
  const [personalData, setPersonalData] = useState({
    title: "",
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    emailAddresses: "",
    mobileNumbers: "",
  });
  const [expensesData, setExpensesData] = useState({
    employment: "",
    nett_monthly_income: "",
    monthly_housing_costs: "",
    other_cost: "",
    monthly_credit_commitments: "",
    pay_frequency: "",
  });
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);

  const personalRef = useRef(null);
  const verifyRef = useRef(null);
  const expensesRef = useRef(null);
  const handleChange = panel => (event, newExpanded) => {
    if (newExpanded) {
      if (panel === "panel1") {
        setMetaTitle({
          metaTitle: "Personal Details",
        });
      }
      if (panel === "panel2") {
        setMetaTitle({
          metaTitle: "Verify Mobile",
        });
      }
      if (panel === "panel3") {
        setMetaTitle({
          metaTitle: "Income and Expenses",
        });
      }
      if (panel === "panel4") {
        setMetaTitle({
          metaTitle: "Account Details",
        });
      }
      if (newExpanded === false) {
        setMetaTitle(metadata);
      }
      setExpanded(newExpanded ? panel : false);
    }
  };

  const getTokens = useCallback(async () => {
    // eslint-disable-next-line no-undef
    grecaptcha.ready(async function () {
      // eslint-disable-next-line no-undef
      grecaptcha
        .execute("6LdUF8caAAAAAI-1Z2LKO8-7VmEOP5H6xI_McvSS", {
          action: "submit",
        })
        .then(async function (token) {
          const body = {
            response: token,
          };
          const score = await fetch(`/api/recaptcha`, {
            method: "POST",
            body: JSON.stringify(body),
          });
          if (score.status !== 200) {
            router.push("/credit-card/error");
          }
        });
    });
  }, [router]);

  useEffect(() => {
    if (!effectHasRun_ref.current) {
      // getTokens();
      effectHasRun_ref.current = true;
    }
  }, [getTokens]);

  useEffect(() => {
    if (personalComplete && !verifyComplete && expanded === "panel2") {
      datadogRum.startView({
        name: "2.2-OTP-Enter-Loaded(Organic)",
      });
    } else if (
      personalComplete &&
      verifyComplete &&
      !expensesComplete &&
      expanded === "panel3"
    ) {
      datadogRum.startView({
        name: "3.1-Income-page-loaded(Organic)",
      });
    } else if (
      personalComplete &&
      verifyComplete &&
      expensesComplete &&
      !applicationSubmitted &&
      expanded === "panel4"
    ) {
      datadogRum.startView({
        name: "5.1-Appsummary-loaded(Organic)",
      });
    }
  }, [
    expanded,
    expensesComplete,
    personalComplete,
    verifyComplete,
    applicationSubmitted,
  ]);

  const metadataWithDefaults = {
    ...global.attributes.metadata,
    ...metaTitle,
  };

  return (
    <Layout
      global={global}
      pageContext={pageContext}
      isModified
      showNav={false}
    >
      {/* Add meta tags for SEO*/}
      <Seo metadata={metadataWithDefaults} />
      <Stepper application={1} />
      {/* <Stepper application={1}/> */}
      <div className="px-5 py-10 md:px-32 md:py-10 bg-onmoLightGrey">
        <motion.div initial={{ y: 100 }} animate={{ y: 0 }}>
          <LargeAccordion
            expanded={expanded === "panel1"}
            onChange={handleChange("panel1")}
          >
            <div ref={personalRef}>
              {expanded !== "panel1" ? (
                <AccordionSummary
                  aria-controls="panel1d-content"
                  id="panel1d-header"
                  tick={personalComplete ? 1 : 0}
                  expanded={expanded === "panel1" ? 1 : 0}
                  lock={applicationSubmitted ? 1 : 0}
                >
                  <p className={styles.largeAccordionTitle}>
                    We need your personal details
                  </p>
                </AccordionSummary>
              ) : null}
            </div>
            <Personal
              verifyComplete={verifyComplete}
              addressData={addressData}
              personalData={personalData}
              completePersonal={(e, personalArray, addressArray) => {
                setPersonalComplete(e);
                setExpanded(
                  expensesComplete
                    ? "panel4"
                    : verifyComplete
                      ? "panel3"
                      : "panel2",
                );
                if (!expensesComplete && !verifyComplete) {
                  datadogRum.addAction("1.3-Personal-details-closed", {
                    onboardingChannel: "Organic",
                  });
                }
                setPersonalData(personalArray);
                setAddressData(addressArray);
                personalRef.current.scrollIntoView();
              }}
            />
          </LargeAccordion>
          <LargeAccordion
            disabled={!personalComplete || verifyComplete}
            expanded={expanded === "panel2"}
            onChange={handleChange("panel2")}
          >
            <div ref={verifyRef}>
              {expanded !== "panel2" ? (
                <AccordionSummary
                  aria-controls="panel2d-content"
                  id="panel2d-header"
                  tick={verifyComplete ? 1 : 0}
                  lock={verifyComplete ? 1 : 0}
                >
                  <p className={styles.largeAccordionTitle}>
                    Verify Mobile Number
                  </p>
                </AccordionSummary>
              ) : null}
            </div>
            <Verify
              mobileNumber={personalData.mobileNumbers}
              verifyComplete={e => {
                setVerifyComplete(e);
                setExpanded("panel3");
                if (e) {
                  datadogRum.addAction("2.5-OTP-Enter-closed", {
                    onboardingChannel: "Organic",
                  });
                }
                verifyRef.current.scrollIntoView();
              }}
            />
          </LargeAccordion>
          <LargeAccordion
            expanded={expanded === "panel3"}
            onChange={handleChange("panel3")}
            disabled={!verifyComplete}
          >
            <div ref={expensesRef}>
              {expanded !== "panel3" ? (
                <AccordionSummary
                  aria-controls="panel3d-content"
                  id="panel3d-header"
                  tick={expensesComplete ? 1 : 0}
                  expanded={expanded === "panel3" ? 1 : 0}
                  lock={applicationSubmitted ? 1 : 0}
                >
                  <p className={styles.largeAccordionTitle}>
                    Income and Expenses
                  </p>
                </AccordionSummary>
              ) : null}
            </div>
            <Expenses
              personalData={personalData}
              addressData={addressData}
              expensesData={expensesData}
              expensesAltButton={expensesComplete}
              expensesComplete={(e, expensesArray) => {
                datadogRum.startView("3.4-Income-page-closed", {
                  onboardingChannel: "Organic",
                });
                setExpenesComplete(e);
                setExpensesData(expensesArray);
                setExpanded("panel4");
                expensesRef.current.scrollIntoView();
              }}
            />
          </LargeAccordion>
          <LargeAccordion
            disabled={!expensesComplete}
            expanded={expanded === "panel4"}
            onChange={handleChange("panel4")}
          >
            {expanded !== "panel4" ? (
              <AccordionSummary
                aria-controls="panel4d-content"
                id="panel4d-header"
              >
                <p className={styles.largeAccordionTitle}>
                  Main account details
                </p>
              </AccordionSummary>
            ) : null}
            <AppSummary
              expensesData={expensesData}
              addressData={addressData}
              personalData={personalData}
              biometricVerification={
                global?.attributes?.biometricVerification ?? false
              }
              changePanel={panel => setExpanded(panel)}
              setApplicationSubmitted={setApplicationSubmitted}
            />
          </LargeAccordion>
        </motion.div>
      </div>
    </Layout>
  );
}

export async function getServerSideProps(context) {
  // Enables parallel testing of new and old onboarding.
  // When this flag is active, appending ?testing=true to the URL will enable the old onboarding.
  if ((await isNewOnboardingEnabled()) && !context.query.testing) {
    return {
      redirect: {
        destination: "/credit-card/application",
      },
    };
  }
  const { locale, locales, defaultLocale } = context;
  const globalLocale = await getGlobalData(locale);

  const pageContext = {
    locale,
    locales,
    defaultLocale,
    slug: context.resolvedUrl.replace(/\//g, ""),
  };

  const localizedPaths = getLocalizedPaths(pageContext);

  const metadata = {
    metaTitle: `Apply for a credit card`,
  };

  return {
    props: {
      metadata,
      global: globalLocale.data,
      pageContext: {
        ...pageContext,
        localizedPaths,
      },
    },
  };
}
